import {
    Button,
    DateRangePicker,
    DateRangePickerProps,
    FormField,
    Grid,
    Link,
    Multiselect,
    Select,
    SpaceBetween,
} from '@amzn/awsui-components-react';
import {BridgeType, NodeFilterData} from './index';
import React from 'react';
import {MAX_ALLOWED_DATE_RANGE} from '../hooks/use-inbound-bridge-filter-data';
import {useHelp} from '../hooks/use-help';
import {HELP_CONTENTS} from '../common/help/help-contents';

export function InboundBridgeFilters({
    bridgeType,
    tenant,
    hideNodeSelect,
    multiSelectNode,
    nodeFilterDataMap,
    selectedFilter,
    setSelectedFilter,
    isLoadingFilterData,
    isLoadingData,
    onApply,
}: {
    bridgeType: BridgeType;
    tenant: {
        business: string;
        country: string;
    };
    hideNodeSelect?: boolean;
    multiSelectNode?: boolean;
    nodeFilterDataMap: Record<
        string,
        {
            balanceDateEarliest: string;
            balanceDateLatest: string;
            horizonPairs: {
                dateUnit: string;
                fromHorizon: number;
                toHorizon: number;
            }[];
            granularityValues: {
                [key: string]: string[];
            };
        }
    >;
    selectedFilter: NodeFilterData | undefined;
    setSelectedFilter: (filter: NodeFilterData) => void;
    isLoadingFilterData: boolean;
    isLoadingData: boolean;
    onApply: () => void;
}) {
    const {showHelp} = useHelp();

    return (
        <SpaceBetween direction="vertical" size="s">
            {renderFirstRow()}
            <Grid gridDefinition={[{colspan: 3}, {colspan: 3}, {colspan: 3}, {colspan: 3}]}>
                {renderGranularityFilterMultiselect('channels', 'Channels')}
                {renderGranularityFilterMultiselect('ibTypes', 'Inbound Types')}
                {renderGranularityFilterMultiselect('tempZones', 'Temp Zones')}
            </Grid>
        </SpaceBetween>
    );

    function renderNodeSelect(): React.ReactElement {
        return (
            <FormField label={tenant.business === 'afs' ? 'Store' : 'Warehouse'}>
                <Select
                    data-testid="page-filter-node-select"
                    options={Object.keys(nodeFilterDataMap).map((node) => ({label: node, value: node}))}
                    selectedOption={{label: selectedFilter?.node, value: selectedFilter?.node}}
                    onChange={({detail}) => {
                        if (detail.selectedOption.value && selectedFilter) {
                            const {balanceDateEarliest, balanceDateLatest, granularityValues} =
                                nodeFilterDataMap[detail.selectedOption.value];
                            setSelectedFilter({
                                ...selectedFilter,
                                node: detail.selectedOption.value,
                                nodes: [detail.selectedOption.value],
                                balanceDateEarliest,
                                balanceDateLatest,
                                channels: granularityValues['channels'] ?? [],
                                sourceLocations: granularityValues['sourceLocations'] ?? [],
                                ibTypes: granularityValues['ibTypes'] ?? [],
                                tempZones: granularityValues['tempZones'] ?? [],
                            });
                        }
                    }}
                />
            </FormField>
        );
    }

    function renderFirstRow(): React.ReactElement {
        if (hideNodeSelect) {
            return (
                <SpaceBetween direction="horizontal" size="s" alignItems={'end'}>
                    {renderDateRangePicker()}
                    {renderHorizonsSelector()}
                    <Button disabled={isLoadingFilterData || isLoadingData} data-testid="page-filter-button" onClick={onApply}>
                        Apply
                    </Button>
                </SpaceBetween>
            );
        } else {
            return (
                <Grid gridDefinition={[{colspan: multiSelectNode ? 2 : 1}, {colspan: 2}, {colspan: 2}, {colspan: 1}]}>
                    {multiSelectNode ? renderNodeMultiSelect() : renderNodeSelect()}
                    {renderDateRangePicker()}
                    {renderHorizonsSelector()}
                    <FormField label={<span>&nbsp;</span>}>
                        <Button disabled={isLoadingFilterData || isLoadingData} data-testid="page-filter-button" onClick={onApply}>
                            Apply
                        </Button>
                    </FormField>
                </Grid>
            );
        }
    }

    function renderNodeMultiSelect(): React.ReactElement {
        const label = tenant.business === 'afs' ? 'Store' : 'Warehouse';
        return (
            <FormField label={label}>
                <Multiselect
                    data-testid="page-filter-node-multiselect"
                    options={Object.keys(nodeFilterDataMap).map((node) => ({label: node, value: node}))}
                    selectedOptions={(selectedFilter?.nodes ?? []).map((n) => ({label: n, value: n}))}
                    onChange={({detail}) => {
                        if (selectedFilter) {
                            const selected = detail.selectedOptions.map((o) => o.value!);
                            const primaryNode = selected[0] ?? selectedFilter.node;
                            const {balanceDateEarliest, balanceDateLatest, granularityValues} =
                                nodeFilterDataMap[primaryNode] ?? {};
                            setSelectedFilter({
                                ...selectedFilter,
                                node: primaryNode,
                                nodes: selected,
                                ...(balanceDateEarliest && {balanceDateEarliest}),
                                ...(balanceDateLatest && {balanceDateLatest}),
                                channels: granularityValues?.['channels'] ?? selectedFilter.channels ?? [],
                                sourceLocations: granularityValues?.['sourceLocations'] ?? selectedFilter.sourceLocations ?? [],
                                ibTypes: granularityValues?.['ibTypes'] ?? selectedFilter.ibTypes ?? [],
                                tempZones: granularityValues?.['tempZones'] ?? selectedFilter.tempZones ?? [],
                            });
                        }
                    }}
                    tokenLimit={6}
                    filteringType="auto"
                    enableSelectAll
                    placeholder={label}
                />
            </FormField>
        );
    }

    function renderHorizonsSelector(): React.ReactElement {
        if (!selectedFilter) {
            return (
                <Select
                    data-testid="page-filter-horizons-select"
                    empty="No options"
                    selectedOption={{label: 'Horizons', value: 'Horizons'}}
                />
            );
        }

        return (
            <FormField
                label={'Horizon'}
                info={
                    <Link
                        variant={'info'}
                        onClick={() => {
                            showHelp(HELP_CONTENTS.INBOUND_BRIDGE_HORIZON);
                        }}
                    >
                        Info
                    </Link>
                }
            >
                <Select
                    data-testid="page-filter-horizons-select"
                    options={nodeFilterDataMap[selectedFilter.node]?.horizonPairs.map((horizonPair) => ({
                        label: buildLabel(horizonPair.fromHorizon, horizonPair.toHorizon),
                        value: `${horizonPair.fromHorizon}-${horizonPair.toHorizon}`,
                    }))}
                    selectedOption={{
                        label: buildLabel(selectedFilter.fromHorizonInWeeks, selectedFilter.toHorizonInWeeks),
                        value: `${selectedFilter.fromHorizonInWeeks}-${selectedFilter.toHorizonInWeeks}`,
                    }}
                    onChange={({detail}) => {
                        if (detail.selectedOption.value) {
                            const [fromHorizon, toHorizon] = detail.selectedOption.value.split('-').map(Number);
                            setSelectedFilter({
                                ...selectedFilter,
                                fromHorizonInWeeks: fromHorizon,
                                toHorizonInWeeks: toHorizon,
                            });
                        }
                    }}
                />
            </FormField>
        );

        function buildLabel(from: number, to: number): string {
            if (bridgeType === BridgeType.P2A) {
                return `T+${from} Week`;
            } else {
                return `T+${from} Week to T+${to} Week`;
            }
        }
    }

    function renderDateRangePicker(): React.ReactElement {
        return (
            <FormField label={'Arrival Dates'}>
                <DateRangePicker
                    data-testid="page-filter-date-range-picker"
                    value={{
                        type: 'absolute',
                        startDate: selectedFilter?.balanceDateStart ?? '',
                        endDate: selectedFilter?.balanceDateEnd ?? '',
                    }}
                    warning={
                        selectedFilter
                            ? selectedFilter.balanceDateStart < selectedFilter.balanceDateEarliest ||
                              selectedFilter.balanceDateEnd > selectedFilter.balanceDateLatest
                            : false
                    }
                    isValidRange={(range) => {
                        if (range?.type === 'absolute' && selectedFilter) {
                            const [startDate] = range.startDate.split('T');
                            const [endDate] = range.endDate.split('T');
                            if (!startDate || !endDate) {
                                return {
                                    valid: false,
                                    errorMessage: 'The selected date range is incomplete.',
                                };
                            }
                            if (startDate < selectedFilter.balanceDateEarliest) {
                                return {
                                    valid: false,
                                    errorMessage: `The selected date range must be after ${selectedFilter.balanceDateEarliest}.`,
                                };
                            }
                            if (endDate > selectedFilter.balanceDateLatest) {
                                return {
                                    valid: false,
                                    errorMessage: `The selected date range must be before ${selectedFilter.balanceDateLatest}.`,
                                };
                            }
                            if (
                                (new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 3600 * 24) >
                                MAX_ALLOWED_DATE_RANGE
                            ) {
                                return {
                                    valid: false,
                                    errorMessage: `The selected date range must not be more than ${MAX_ALLOWED_DATE_RANGE} days.`,
                                };
                            }
                        }
                        return {valid: true};
                    }}
                    onChange={({detail}) => {
                        if (selectedFilter) {
                            setSelectedFilter({
                                ...selectedFilter,
                                balanceDateStart: (detail.value as DateRangePickerProps.AbsoluteValue).startDate,
                                balanceDateEnd: (detail.value as DateRangePickerProps.AbsoluteValue).endDate,
                            });
                        }
                    }}
                    dateOnly={true}
                    relativeOptions={[]}
                    placeholder="Filter by a date range"
                    rangeSelectorMode="absolute-only"
                    i18nStrings={{
                        applyButtonLabel: 'Apply',
                        cancelButtonLabel: 'Cancel',
                        clearButtonLabel: 'Clear and dismiss',
                    }}
                />
            </FormField>
        );
    }

    function renderGranularityFilterMultiselect(granularity: string, display: string): React.ReactElement {
        return (
            <Multiselect
                data-testid={`page-filter-${granularity}-multiselect`}
                selectedOptions={
                    selectedFilter?.[granularity]?.map((gran) => ({
                        label: gran,
                        value: gran,
                    })) ?? []
                }
                onChange={async ({detail}) => {
                    if (selectedFilter) {
                        setSelectedFilter({
                            ...selectedFilter,
                            [granularity]: detail.selectedOptions.map((item) => item.value!),
                        });
                    }
                }}
                options={
                    selectedFilter
                        ? nodeFilterDataMap[selectedFilter?.node]?.granularityValues[granularity]?.map((gran) => ({
                              label: gran,
                              value: gran,
                          })) ?? []
                        : []
                }
                tokenLimit={5}
                enableSelectAll
                filteringType="auto"
                placeholder={display}
            />
        );
    }
}
